import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { MeResponse, ModuleItem } from '@kassa/shared';
import { api } from '../api';
import { hasPermission } from '../format';
import { groupOf, menuGroups, NAV_ICON, sectionOf, type NavGroupKey, type NavKey } from '../nav';
import { searchNorm } from '../../../../packages/shared/src/search';
import { CommandPalette } from './CommandPalette';

// Qabıq (KT-108, maket 25; KT-99-u əvəz edir): tünd sol menyu tam hündürlükdə — bölmə başlıqları bölmə rəngində (SATIŞ, MAL VƏ
// ANBAR …), ikonlu bəndlər, «Menyuda axtar», «Menyunu daralt»; üst sətir — yol, obyekt, Ctrl+K axtarış, skaner, «+ Yeni»
// (F2/F3), istifadəçi. Paketdə olmayan modul və icazəsiz bənd göstərilmir (nav.ts).

export type ShellTarget = NavKey | 'home';

interface Props {
  me: MeResponse;
  /** Aktiv bölmə; 'home' — ana səhifə. */
  active: ShellTarget;
  onNavigate: (to: ShellTarget) => void;
  /** Ctrl+K nəticəsi və «+ Yeni»: mal/müştəri kartı (null — yeni) */
  onOpen: (kind: 'product' | 'customer', id: string | null) => void;
  onChangeOutlet: () => void;
  onSwitchCompany: (() => void) | null;
  onOpenAccount: () => void;
  onLogout: () => void;
  children: ReactNode;
}

// Modul siyahısı obyekt üzrə yüklənir və 5 dəqiqə saxlanır (hər ekran keçidində yox; admin modulu açanda da yenilənir)
const MODULES_TTL = 5 * 60_000;
let modulesCache: { outletId: string | null; modules: ModuleItem[]; at: number } | null = null;

function useModules(outletId: string | null): ModuleItem[] | null {
  const cached = modulesCache && modulesCache.outletId === outletId && Date.now() - modulesCache.at < MODULES_TTL ? modulesCache.modules : null;
  const [modules, setModules] = useState<ModuleItem[] | null>(cached);
  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    api
      .modules()
      .then((r) => {
        modulesCache = { outletId, modules: r.modules, at: Date.now() };
        if (!cancelled) setModules(r.modules);
      })
      .catch(() => !cancelled && setModules([]));
    return () => {
      cancelled = true;
    };
  }, [outletId]);
  return modules;
}

export function AppShell({ me, active, onNavigate, onOpen, onChangeOutlet, onSwitchCompany, onOpenAccount, onLogout, children }: Props) {
  const modules = useModules(me.outlet?.id ?? null);
  // Modullar gələnə qədər qruplar göstərilmir: modula bağlı bənd sonradan peyda olub menyunu sürüşdürməsin
  const groups = modules === null ? [] : menuGroups(me.permissions, modules);
  const visible = groups.flatMap((g) => g.items);
  const section = active === 'home' ? null : sectionOf(active);
  const activeGroup: NavGroupKey | null = section?.group ?? null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState(() => readFlag(COLLAPSE_KEY));
  // «Sınaq bitdi — paket seçin» (KT-85): yalnız paketi idarə edən (settings.subscription) görür; satış dayanmır
  const canPlan = hasPermission(me, 'settings.subscription');
  const [trialEnded, setTrialEnded] = useState<string | null>(null);
  useEffect(() => {
    if (!canPlan || !me.tenant) return;
    let live = true;
    api
      .trial()
      .then((t) => live && setTrialEnded(t.ended && t.trialUntil ? t.trialUntil : null))
      // Xətada əvvəlki vəziyyət qalır (göstərilmiş zolaq səhv sorğu ilə yox olmasın)
      .catch(() => console.warn('trial state failed'));
    return () => {
      live = false;
    };
  }, [canPlan, me.tenant, me.outlet?.id]);

  const go = (to: ShellTarget) => {
    setMenuOpen(false);
    setNewOpen(false);
    // Əvvəlki siyahının süzgəci (?q=…) yeni bölməyə keçməsin
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
    onNavigate(to);
  };
  const can = (k: NavKey) => visible.some((v) => v.key === k);

  // «+ Yeni» (icazəyə görə) və qısayollar: F2 — yeni satış, F3 — malı aldım, Ctrl+K — axtarış
  const quick: { label: string; key: string; run: () => void; ok: boolean }[] = [
    { label: 'Yeni satış', key: 'F2', run: () => go('sales'), ok: can('sales') },
    { label: 'Malı aldım', key: 'F3', run: () => go('intake'), ok: can('intake') },
    { label: 'Yeni məhsul', key: '', run: () => (setNewOpen(false), onOpen('product', null)), ok: hasPermission(me, 'product.edit') },
    { label: 'Yeni ödəniş', key: '', run: () => go('financePayment'), ok: can('financePayment') },
    { label: 'Yeni müştəri', key: '', run: () => (setNewOpen(false), onOpen('customer', null)), ok: can('customers') && hasPermission(me, 'customer.edit') },
  ];
  const quickRef = useRef({ quick, active, palette });
  quickRef.current = { quick, active, palette };
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      // Açıq pəncərə (təsdiq, Ctrl+K) üstündə qısayol işləmir — altdakı ekran dəyişməsin
      if (quickRef.current.palette || document.querySelector('[aria-modal="true"]')) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPalette(true);
        return;
      }
      const q = quickRef.current.quick.find((x) => x.key && x.key === e.key && x.ok);
      if (!q) return;
      e.preventDefault();
      // Artıq həmin ekrandadırsa heç nə etmə: yarımçıq səbət (satış) yenidən qurulub itməsin
      const target = q.key === 'F2' ? 'sales' : q.key === 'F3' ? 'intake' : null;
      if (target && quickRef.current.active === target) return;
      q.run();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  async function logout() {
    await api.logout().catch(() => undefined);
    onLogout();
  }

  const toggleCollapse = () => {
    setCollapsed((v) => {
      writeFlag(COLLAPSE_KEY, !v);
      return !v;
    });
  };
  const n = searchNorm(filter.trim());
  const shown = groups
    .map(({ group, items }) => ({ group, items: n ? items.filter((i) => searchNorm(i.label).includes(n) || searchNorm(group.label).includes(n)) : items }))
    .filter((g) => g.items.length > 0);
  const initial = (me.accountName || '?').trim().charAt(0).toUpperCase();

  return (
    <div className={`app app2${collapsed ? ' is-collapsed' : ''}`}>
      <nav id="app-nav" className={`app-nav app2-nav${menuOpen ? ' is-open' : ''}`} aria-label="Bölmələr" aria-busy={modules === null}>
        <div className="app2-brand">
          <span className="app-logo" aria-hidden="true">
            K
          </span>
          <span className="app2-brand-text">
            <span className="app-name">Kassa</span>
            <span className="app-company">{me.tenant?.tenantName}</span>
          </span>
        </div>
        {(!collapsed || menuOpen) && (
          <input className="app2-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Menyuda axtar" aria-label="Menyuda axtar" type="search" />
        )}
        <button type="button" className={`app2-item${active === 'home' ? ' is-active' : ''}`} aria-current={active === 'home' ? 'page' : undefined} aria-label="Ana səhifə" title="Ana səhifə" onClick={() => go('home')}>
          <Icon d="M3 11l9-7 9 7 M5 10v10h14V10 M10 20v-6h4v6" />
          <span className="app2-label">Ana səhifə</span>
        </button>
        {shown.map(({ group, items }) => (
          <div key={group.key} className={`app2-group sec-${group.key}${activeGroup === group.key ? ' is-active' : ''}`} role="group" aria-label={group.label}>
            <div className="app2-group-head" aria-hidden="true">
              {group.label}
            </div>
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                className={`app2-item${active === it.key ? ' is-active' : ''}`}
                aria-current={active === it.key ? 'page' : undefined}
                aria-label={it.label}
                title={collapsed ? it.label : undefined}
                onClick={() => go(it.key)}
              >
                <Icon d={NAV_ICON[it.key] ?? group.icon} className="app-nav-icon" />
                <span className="app2-label">{it.label}</span>
              </button>
            ))}
          </div>
        ))}
        {n && shown.length === 0 && <p className="app2-empty">Tapılmadı</p>}
        <button type="button" className="app2-item app2-collapse" aria-pressed={collapsed} aria-label={collapsed ? 'Menyunu genişlət' : 'Menyunu daralt'} title={collapsed ? 'Menyunu genişlət' : undefined} onClick={toggleCollapse}>
          <Icon d={collapsed ? 'M9 6l6 6-6 6' : 'M15 6l-6 6 6 6'} />
          <span className="app2-label">Menyunu daralt</span>
        </button>
      </nav>
      {menuOpen && <div className="app2-scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />}

      <div className="app2-col">
        <header className="app2-top">
          <button type="button" className="app-burger" aria-expanded={menuOpen} aria-controls="app-nav" aria-label="Menyu" onClick={() => setMenuOpen((v) => !v)}>
            <Icon d="M4 6h16 M4 12h16 M4 18h16" />
          </button>
          <nav className="app2-crumbs" aria-label="Yol">
            <button type="button" className="app-crumb-link" onClick={() => go('home')}>
              Kassa
            </button>
            {section && (
              <>
                <span aria-hidden="true"> / </span>
                <span className="app2-crumb-group">{groupOf(section.group).label}</span>
                <span aria-hidden="true"> / </span>
              </>
            )}
            {!section && <span aria-hidden="true"> / </span>}
            <span className="app-crumb-here">{section ? section.label : 'Ana səhifə'}</span>
          </nav>
          {me.outlet && (
            <button type="button" className="app-outlet app2-outlet" onClick={onChangeOutlet} aria-label={`Obyekt: ${me.outlet.name} — dəyiş`}>
              {me.outlet.name}
              <Icon d="M6 9l6 6 6-6" size={14} />
            </button>
          )}
          <button type="button" className="app2-search" onClick={() => setPalette(true)} aria-label="Axtarış (Ctrl+K)">
            <Icon d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z M21 21l-4.3-4.3" size={16} />
            <span className="app2-search-text">Mal, müştəri, sənəd axtar…</span>
            <kbd>Ctrl K</kbd>
          </button>
          <span className="app-spacer" />
          {can('scanner') && (
            <button type="button" className="app-icon-btn app2-icon" aria-label="Skaner" title="Skaner" onClick={() => go('scanner')}>
              <Icon d="M4 8V5h3 M17 5h3v3 M20 16v3h-3 M7 19H4v-3 M7 12h10" />
            </button>
          )}
          {quick.some((q) => q.ok) && (
            <div className="app-user">
              {/* Dar ekranda «Yeni» mətni gizlənir (yalnız ikon) — ad aria-label-dən (axe button-name, KT-107) */}
              <button type="button" className="app-new-sale" aria-label="Yeni" aria-haspopup="menu" aria-expanded={newOpen} onClick={() => setNewOpen((v) => !v)}>
                <Icon d="M12 5v14 M5 12h14" size={16} />
                <span>Yeni</span>
              </button>
              {newOpen && (
                <div className="app-user-menu" role="menu" aria-label="Yeni">
                  {quick
                    .filter((q) => q.ok)
                    .map((q) => (
                      <button key={q.label} type="button" role="menuitem" onClick={q.run}>
                        <span>{q.label}</span>
                        {q.key && <kbd className="app2-kbd">{q.key}</kbd>}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
          <div className="app-user">
            <button type="button" className="app-user-btn" aria-label="İstifadəçi menyusu" aria-expanded={userOpen} aria-haspopup="menu" onClick={() => setUserOpen((v) => !v)}>
              <span className="app-avatar" aria-hidden="true">
                {initial}
              </span>
              <span className="app-user-name">{me.accountName}</span>
            </button>
            {userOpen && (
              <div className="app-user-menu" role="menu">
                <button type="button" role="menuitem" onClick={() => (setUserOpen(false), onOpenAccount())}>
                  Hesabım
                </button>
                {hasPermission(me, 'settings.users') && (
                  <button type="button" role="menuitem" onClick={() => (setUserOpen(false), go('settings'))}>
                    Tənzimləmə
                  </button>
                )}
                {onSwitchCompany && (
                  <button type="button" role="menuitem" onClick={() => (setUserOpen(false), onSwitchCompany())}>
                    Şirkəti dəyiş
                  </button>
                )}
                <button type="button" role="menuitem" onClick={() => (setUserOpen(false), onChangeOutlet())}>
                  Obyekti dəyiş
                </button>
                <button type="button" role="menuitem" onClick={() => void logout()}>
                  Çıxış
                </button>
              </div>
            )}
          </div>
        </header>

        {trialEnded && (
          <div className="trial-banner" role="status">
            <span>
              Sınaq {trialEnded.slice(8, 10)}.{trialEnded.slice(5, 7)}.{trialEnded.slice(0, 4)} tarixində bitdi — paket seçin. Kassa işləməyə davam edir.
            </span>
            <button type="button" className="btn-small" onClick={() => go('settings')}>
              Paket seç
            </button>
          </div>
        )}

        <div className={`app-main${activeGroup ? ` sec-${activeGroup}` : ' is-home'}`}>{children}</div>
      </div>

      {palette && (
        <CommandPalette
          me={me}
          sections={visible}
          onClose={() => setPalette(false)}
          onPick={(h) => {
            setPalette(false);
            if (h.kind === 'nav') go(h.section.key);
            else onOpen(h.kind, h.item.id);
          }}
        />
      )}
    </div>
  );
}

const COLLAPSE_KEY = 'kassa.nav.collapsed';
function readFlag(k: string): boolean {
  try {
    return window.localStorage.getItem(k) === '1';
  } catch {
    return false;
  }
}
function writeFlag(k: string, v: boolean): void {
  try {
    window.localStorage.setItem(k, v ? '1' : '0');
  } catch {
    /* yaddaş yoxdur — seçim yalnız bu səhifədə */
  }
}

export function Icon({ d, size = 20, className }: { d: string; size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d={d} />
    </svg>
  );
}
